/**
 * admin/AdminSecurity.js — your own two-step sign-in.
 *
 * Set up with any authenticator app (scan a QR code, type one code to
 * prove it works), keep the ten recovery codes somewhere safe, or turn it
 * off with your password. When STAFF_2STEP=required, anyone without it is
 * brought here first and can't use the rest of the panel until it's on.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { HiOutlineShieldCheck, HiOutlineDevicePhoneMobile, HiOutlineKey } from "react-icons/hi2";
import AdminLayout from "./AdminLayout";
import { authAPI, setToken } from "../services/api";
import "./AdminSecurity.css";

export default function AdminSecurity() {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const [setup, setSetup] = useState(null);       // { qr, secret }
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState(null);       // recovery codes, shown once
  const [busy, setBusy] = useState(false);
  const [off, setOff] = useState(false);
  const [pw, setPw] = useState("");
  const navigate = useNavigate();

  const load = () => authAPI.twoStep.status().then((r) => setStatus(r.data)).catch((e) => setError(e.message));
  useEffect(() => { load(); }, []);

  const start = async () => {
    setBusy(true); setError("");
    try { const r = await authAPI.twoStep.setup(); setSetup(r.data); setCode(""); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const confirm = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const r = await authAPI.twoStep.enable(code.trim());
      if (r.data.token) setToken(r.data.token);
      setCodes(r.data.recoveryCodes);
      setSetup(null);
      load();
      toast.success("Two-step sign-in is on.");
    } catch (e2) { setError(e2.message); }
    finally { setBusy(false); }
  };

  const turnOff = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try { await authAPI.twoStep.disable(pw); setOff(false); setPw(""); load(); toast.success("Two-step sign-in is off."); }
    catch (e2) { setError(e2.message); }
    finally { setBusy(false); }
  };

  const download = () => {
    const blob = new Blob([`Recovery codes for your admin sign-in. Each works once.\n\n${codes.join("\n")}\n`], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "admin-recovery-codes.txt"; a.click();
    URL.revokeObjectURL(a.href);
  };
  const copy = () => { try { navigator.clipboard.writeText(codes.join("\n")); toast.success("Copied."); } catch (_) { /* no clipboard */ } };

  return (
    <AdminLayout pageTitle="Security" pageSubtitle="Two-step sign-in for your admin account.">
      <div className="asec">
        {status?.required && !status.enabled && !codes && (
          <p className="asec__banner" role="alert">Your team requires two-step sign-in. Set it up below to continue to the rest of the panel.</p>
        )}
        {error && <p className="asec__error" role="alert">{error}</p>}

        {codes ? (
          <section className="acard asec__card">
            <h2 className="asec__title"><HiOutlineKey aria-hidden="true" /> Save your recovery codes</h2>
            <p className="asec__text">If you lose your phone, each of these lets you sign in once. This is the only time they're shown. Keep them somewhere safe, like your password manager.</p>
            <ol className="asec__codes">{codes.map((c) => <li key={c}><code>{c}</code></li>)}</ol>
            <div className="asec__actions">
              <button type="button" className="a-btn a-btn--outline" onClick={copy}>Copy</button>
              <button type="button" className="a-btn a-btn--outline" onClick={download}>Download .txt</button>
              <button type="button" className="a-btn a-btn--rust" onClick={() => { setCodes(null); navigate("/admin/dashboard"); }}>I've saved them</button>
            </div>
          </section>
        ) : setup ? (
          <section className="acard asec__card">
            <h2 className="asec__title"><HiOutlineDevicePhoneMobile aria-hidden="true" /> Set up two-step sign-in</h2>
            <ol className="asec__steps">
              <li>Open an authenticator app on your phone (Google Authenticator, Microsoft Authenticator, 1Password, Authy…).</li>
              <li>Add an account and scan this code.
                <div className="asec__qr">
                  <img src={setup.qr} alt="QR code to add this admin account to your authenticator app" width={200} height={200} />
                  <p className="asec__manual">Can't scan? Enter this key instead:<code>{setup.secret}</code></p>
                </div>
              </li>
              <li>
                <form onSubmit={confirm} className="asec__form">
                  <label className="asec__label" htmlFor="asec-code">Type the 6-digit code the app shows</label>
                  <div className="asec__row">
                    <input id="asec-code" className="asec__input" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} autoFocus
                      onChange={(e) => { setCode(e.target.value.replace(/\D/g, "")); setError(""); }} placeholder="123456" />
                    <button className="a-btn a-btn--rust" disabled={busy || code.length < 6}>{busy ? "Checking…" : "Turn on"}</button>
                  </div>
                </form>
              </li>
            </ol>
            <button type="button" className="a-btn a-btn--ghost" onClick={() => setSetup(null)}>Cancel</button>
          </section>
        ) : status && (
          <section className="acard asec__card">
            <h2 className="asec__title"><HiOutlineShieldCheck aria-hidden="true" /> Two-step sign-in is {status.enabled ? "on" : "off"}</h2>
            {status.enabled ? (
              <>
                <p className="asec__text">
                  Signing in asks for your password and a code from your authenticator app.
                  {" "}{status.recoveryCodesLeft} of 10 recovery codes left{status.recoveryCodesLeft < 4 ? ": turn two-step off and on again for a new set." : "."}
                </p>
                {!status.required && (off ? (
                  <form onSubmit={turnOff} className="asec__form">
                    <label className="asec__label" htmlFor="asec-pw">Your password, to turn it off</label>
                    <div className="asec__row">
                      <input id="asec-pw" type="password" className="asec__input" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="current-password" autoFocus />
                      <button className="a-btn a-btn--outline" disabled={busy || !pw}>Turn off</button>
                      <button type="button" className="a-btn a-btn--ghost" onClick={() => { setOff(false); setPw(""); }}>Cancel</button>
                    </div>
                  </form>
                ) : (
                  <button type="button" className="a-btn a-btn--outline" onClick={() => setOff(true)}>Turn off two-step</button>
                ))}
                {status.required && <p className="asec__text asec__muted">Your team requires it, so it stays on. If you get a new phone, ask a super admin to reset it, then set it up again.</p>}
              </>
            ) : (
              <>
                <p className="asec__text">Add a second step to signing in: after your password, a 6-digit code from an app on your phone. Someone who learns your password still can't get in.</p>
                <button type="button" className="a-btn a-btn--rust" disabled={busy} onClick={start}>{busy ? "Starting…" : "Set it up"}</button>
              </>
            )}
          </section>
        )}
      </div>
    </AdminLayout>
  );
}
